import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMobile } from "@ff-os/shared-react/hooks/useMobile";
import { urls } from "@ff-os/shared-utils/urls";
import classNames from "classnames";
import {
    setFrameId,
    selectFrameId,
    setAccessoryId,
    setIsFotofunBannerHidden,
    setFotoFunTab,
    setFrameIsDragging
} from "../../../_redux-ui";
import { DragCardHover } from "../components/DragCardHover";
import { MagicLabelIcon } from "./MagicLabelIcon";
import styles from "./DraggableImageSquare.module.scss";

interface DraggableImageSquareProps {
    className?: string;
    onDragStart?: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragEnd?: (event: React.DragEvent<HTMLDivElement>) => void;
    effect?: any;
}

const DraggableImageSquareComponent = ({
    className = "",
    onDragStart,
    onDragEnd,
    effect
}: DraggableImageSquareProps) => {

    const [isLocalDragging, setIsLocalDragging] = React.useState(false);

    const dispatch = useDispatch();
    const isMobile = useMobile();

    const frameIdMobile = useSelector(selectFrameId);
    const dataId = effect.id;
    const isMagicEffect = !!effect.isMagic;
    const isActiveFrame = frameIdMobile === dataId && isMobile;
    const isActiveClickToApply = frameIdMobile === dataId && !isMobile;

    let imageUrl;

    if (effect.iconassetId) {
        imageUrl = urls.publicAssetUrl(effect.iconassetId);
    } else {
        imageUrl = urls.publicAssetUrl(effect.assetId);
    }

    const setDragging = (dragging: boolean) => {
        setIsLocalDragging(dragging);
        dispatch(setFrameIsDragging(dragging));
    };

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {

        event.dataTransfer.setData("text/plain", "frame");
        event.dataTransfer.setData("frameId", dataId);

        if (isMagicEffect) {
            event.dataTransfer.setData("isMagicEffect", isMagicEffect.toString());
        }

        // to center the image while dragging in a scaled window
        event.dataTransfer.setDragImage(
            event.currentTarget,
            event.currentTarget.clientWidth / 2,
            event.currentTarget.clientHeight / 2
        );

        dispatch(setFrameId(""));
        dispatch(setAccessoryId(""));

        setDragging(true);
        onDragStart?.(event);
    };

    const handleDragEnd = (event: React.DragEvent<HTMLDivElement>) => {

        setDragging(false);
        onDragEnd?.(event);
    };

    const handleClick = () => {

        if (isMobile) {
            dispatch(setFotoFunTab("magical"));
            //dispatch(setIsFotofunBannerHidden(true));
            dispatch(setFrameId(isActiveFrame ? "" : dataId));
            dispatch(setAccessoryId(""));
        } else {
            // Desktop click-to-apply logic using unified mobile state
            dispatch(setFrameId(isActiveClickToApply ? "" : dataId));
            dispatch(setAccessoryId(""));
        }
    };

    const squareClassNames = classNames(
        styles.draggableSquare, className,
        {
            [styles.activeSquare]: isActiveFrame || isActiveClickToApply
        }
    );

    const placeHolderClassNames = classNames(
        styles.imagePlaceholder,
        {
            [styles.magicEffect]: isMagicEffect,
            [styles.standardEffect]: !isMagicEffect
        }
    );

    return (
        <div
            className={squareClassNames}
            draggable={!isMobile}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            data-id={dataId}
            onClick={handleClick}
        >
            <div className={placeHolderClassNames}>
                <div
                    style={{ backgroundImage: `url(${imageUrl})` }}
                    className={styles.image}
                    draggable="false"
                />
            </div>

            <DragCardHover isDragging={isLocalDragging} frameId={dataId} isClickActive={isActiveFrame || isActiveClickToApply}/>

            {isMagicEffect && <MagicLabelIcon />}
        </div>
    );
};

export const DraggableImageSquare = React.memo(DraggableImageSquareComponent);